import type { CSSProperties, ReactNode } from 'react';

import type { StorefrontCategory } from '@/features/storefront/api/storefront';
import { StorefrontImage } from '@/features/storefront/components/StorefrontImage';
import { StorefrontContainer as Container } from '@/features/storefront/components/StorefrontContainer';
import type { CategoryStripSection } from '@/features/storefront/sections/schema';
import { cn } from '@/lib/cn';

/**
 * One thin row of small icon-led department links - the strip a grocery
 * marketplace runs just under its header. The links are always the shop's own
 * top-level categories, at most `max_items` of them; each uses the category's
 * picture as its icon, or its initial letter when it has none.
 *
 * The row scrolls sideways inside itself on a narrow screen rather than
 * wrapping or pushing the page wider. Every link is a real link, so the row
 * is walked with Tab like any other.
 */

/** How a link is drawn: the caller's own, so preview links stay inert. */
export type CategoryStripLink = (href: string, className: string, children: ReactNode) => ReactNode;

const ICON_SIZE: Record<CategoryStripSection['icon_size'], string> = {
  sm: 'size-10',
  md: 'size-14',
};

const ICON_SIZES_ATTR: Record<CategoryStripSection['icon_size'], string> = {
  sm: '40px',
  md: '56px',
};

const DIVIDER: CSSProperties = {
  borderColor: 'color-mix(in srgb, currentColor 14%, transparent)',
};

/** The shop's top-level categories, in their order, cut to the section's count. */
export function categoryStripItems(
  section: Pick<CategoryStripSection, 'max_items'>,
  categories: readonly StorefrontCategory[],
): StorefrontCategory[] {
  return categories.filter((category) => !category.parent_id).slice(0, section.max_items);
}

function StripIcon({ section, category }: { section: CategoryStripSection; category: StorefrontCategory }) {
  if (section.icon_shape === 'none') return null;
  const shape = section.icon_shape === 'circle' ? 'rounded-full' : 'rounded-md';
  return (
    <span
      aria-hidden
      data-strip-icon
      className={cn(
        'flex shrink-0 items-center justify-center overflow-hidden',
        ICON_SIZE[section.icon_size],
        shape,
      )}
      style={{ backgroundColor: 'var(--storefront-surface)' }}
    >
      {category.image_url ? (
        <StorefrontImage
          url={category.image_url}
          sizes={ICON_SIZES_ATTR[section.icon_size]}
          alt=""
          className="size-full object-cover"
        />
      ) : (
        <span
          data-strip-initial
          className="text-sm font-semibold uppercase"
          style={{ color: 'var(--storefront-muted)' }}
        >
          {category.name.trim().charAt(0) || '•'}
        </span>
      )}
    </span>
  );
}

export function CategoryStripView({
  section,
  categories,
  link,
  heading,
  emptyText,
}: {
  section: CategoryStripSection;
  categories: readonly StorefrontCategory[];
  link: CategoryStripLink;
  /** The section's title, drawn by the caller; null when it has none. */
  heading: ReactNode;
  /** What stands in the row while the shop has no categories. */
  emptyText: ReactNode;
}) {
  const items = categoryStripItems(section, categories);
  const beside = section.label_position === 'beside' && section.icon_shape !== 'none';

  return (
    <Container>
      {heading}
      {items.length === 0 ? (
        <p className="text-sm" style={{ color: 'var(--storefront-muted)' }} data-strip-empty>
          {emptyText}
        </p>
      ) : (
        <nav aria-label={section.title || undefined} className="min-w-0 max-w-full">
          <ul
            className={cn(
              // Scrolls inside itself on a phone; centred when it all fits. Auto
              // margins, not justify-center, which would push the first links
              // past the left edge, out of reach, once the row overflows.
              'flex max-w-full snap-x gap-2 overflow-x-auto overscroll-x-contain pb-1 [scrollbar-width:thin]',
              'sm:[&>li:first-child]:ms-auto sm:[&>li:last-child]:me-auto',
            )}
          >
            {items.map((category, index) => (
              <li
                key={category.id}
                className={cn(
                  'shrink-0 snap-start',
                  section.item_dividers && index > 0 && 'border-l pl-2',
                )}
                style={section.item_dividers ? DIVIDER : undefined}
              >
                {link(
                  `/products?category=${category.slug}`,
                  cn(
                    'group flex rounded-md p-1.5 outline-offset-2 focus-visible:outline-2',
                    beside
                      ? 'flex-row items-center gap-2 pr-3'
                      : 'w-20 flex-col items-center gap-1.5 text-center',
                    section.icon_shape === 'none' && 'px-3 py-2',
                  ),
                  <>
                    <StripIcon section={section} category={category} />
                    <span
                      className={cn(
                        'text-xs font-medium leading-tight transition-colors',
                        'group-hover:text-[var(--storefront-primary)] group-focus-visible:text-[var(--storefront-primary)]',
                        beside ? 'whitespace-nowrap' : 'line-clamp-2 break-words',
                      )}
                    >
                      {category.name}
                    </span>
                  </>,
                )}
              </li>
            ))}
          </ul>
        </nav>
      )}
    </Container>
  );
}
